<template>
  <div class="ink-slab">
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The site's one dark moment on a light page, set in from the section edges: a
   full-bleed dark band already owns the other way of being dark, so this is the
   contained one. Same ground, different weight.

   Its contents are the page's business and have been since it existed: what is
   shared is the object, not what sits in it. What every use does share is the
   rule it imposes on its contents, which is that the slab owns the surface: the
   title inside it is `tone="dark"`, and the ruled list under it is a `bare`
   PanelList, because a panel that painted its own fill here would be a second
   surface inside the first.

   This was two identical declarations until the portfolio case page needed a
   third: `.brief-panel` in Promotions/Sections/Brief.vue and `.bp-slip` in
   Services/BrandPresence.vue, byte for byte the same ground, glow, radius and
   padding under two names, with a comment in each pointing at the other and
   saying they read as one family. Two copies that agree in the comment and are
   maintained apart is the drift this stops; the third use is what made it worth
   naming. */
.ink-slab {
  /* The glow is the slab's own background rather than a `::before` layer over
     it. Both copies this replaces reached for a layer, and a layer means the
     contents have to be lifted back over it: they carried a `> *` rule for that,
     which stops working the moment the slab is a component, because slot content
     is compiled in the parent that supplies it and never carries this
     component's scope id, so `> *` matches none of it. As a background there is
     no layer to get under: it paints where the ink does, and everything the page
     puts in arrives above it with nothing declared.
     `background-origin` defaults to the padding box, so the light starts at the
     same corner the layer's `inset: 0` gave it. */
  background-color: $ink;
  background-image: radial-gradient(42rem 24rem at 0% 0%, rgba($primary, .38), transparent 62%);
  border-radius: $r-xl;
  /* Clipping only, not the glow: a background is trimmed by the radius on its
     own. This keeps a child that would sit outside the corner inside the slab. */
  overflow: hidden;
  padding: 2.5rem 1.5rem;
  /* The pointer response's own two properties. $dur is the step the tokens keep
     for transform and shadow, which is exactly the pair below. */
  transition: box-shadow $dur $ease, transform $dur $ease;

  @media (min-width: 768px) {
    padding: 3rem 2.5rem;
  }

  @media (min-width: 1200px) {
    padding: 3.5rem 3.25rem;
  }

  /* The pointer response, which the slab did not have.

     It was the one surface on the site a reader could point at and get nothing
     back from, while every panel beside it answers. It answers in the
     vocabulary `panel-response` already sets, which is three things together:
     the hairline brightens, a shadow arrives, the box lifts. A slab with no
     border cannot brighten one, so the lift and the shadow are kept and the
     line is drawn as an inset ring instead.

     The ring is $border-primary, the same value the hero's floating card lights
     its edge with, and an inset shadow follows the border radius on its own, so
     the ring takes the slab's own corner rather than needing a second rounded
     box behind it.

     The other two are deliberately a step lighter than a panel's, because this
     is the largest object on any page that carries one: a full-width, 500px
     tall block answering a pointer that merely crosses it. So the shadow is
     $shadow-hover rather than the $shadow-lift a card takes, and the travel is
     2px rather than 3px. $shadow-hover is the token the site keeps for exactly
     this case - the glow under a hovered thing, roughly half the reach at half
     the alpha of a resting glow - and it being primary-tinted means the spill
     is the same blue the slab's corner already throws, so the hover deepens the
     light the object already has rather than introducing a second one. The 2px
     is the site's other lift step, the one the plan and brand-kit boxes use.

     `:focus-within` is here for the same reason the panels carry it: the case
     page's slab holds the link to the live site, so a keyboard reader tabbing
     onto that link gets the same confirmation a pointer would.

     This is the component's response, not the case page's, so the promo brief
     and the brand-order slip answer the pointer the same way. That is the point
     of it living here: three callers, one behaviour, and no page able to give
     the slab a hover of its own. */
  &:hover,
  &:focus-within {
    box-shadow: $shadow-hover, inset 0 0 0 1px $border-primary;
    transform: translateY(-2px);
  }
}
</style>
